import type { GeometryFeature } from "@mappedin/mvf/core";
import type { DefaultStyle } from "@mappedin/mvf/default-style";
import type { Location, LocationInstance } from "@mappedin/mvf/locations";
import buffer from "@turf/buffer";
import pointOnFeature from "@turf/point-on-feature";
import type { Feature, FeatureCollection, Geometry, Point } from "geojson";
import type { MappedinStyleCollection } from "velavu-whitelabel";
import { readMVFBundle } from "./mappedin-mvf-parser";

type MVFGeometryAnchor = DefaultStyle["geometryAnchors"][number];

/**
 * Properties of a geometry feature in the MVF data,
 * derived from metadata in the MVF bundle.
 */
export interface MappedinMVFGeometryProperties {
	color: string;
	opacity: number;
	height: number;
	altitude: number;
}

/**
 * Properties of a label feature in the MVF data,
 * derived from metadata in the MVF bundle.
 */
export interface MappedinMVFLabelProperties {
	text: string;
}

/**
 * A single MVF floor
 */
export interface MappedinMVFFloor {
	id: string;
	elevation?: number;
	geometry: FeatureCollection<Geometry, MappedinMVFGeometryProperties>;
	labels: FeatureCollection<Point, MappedinMVFLabelProperties>;
}

/**
 * The MVF data for a venue
 */
export interface MappedinMVFVenue {
	floors: Record<string, MappedinMVFFloor>;
}

const MAPPEDIN_API_BASE_URL = "https://app.mappedin.com/api/v1";
const MAPPEDIN_MVF_VERSION = "3.0.0";

interface MappedinMVFDownloadResponse {
	url: string;
	updated_at?: string;
	locale_packs?: Record<string, string>;
}

/**
 * Fetches the MVF metadata for a venue from Mappedin
 * @param venueID The ID of the venue to fetch the MVF metadata for
 * @param accessToken The access token to use to fetch the MVF metadata
 * @returns The MVF metadata for the venue
 * @throws An error if the MVF metadata cannot be fetched
 */
async function fetchMappedinVenueMVF(
	venueID: string,
	accessToken: string,
): Promise<MappedinMVFDownloadResponse> {
	const response = await fetch(
		`${MAPPEDIN_API_BASE_URL}/venue/${encodeURIComponent(
			venueID,
		)}/mvf?version=${MAPPEDIN_MVF_VERSION}`,
		{
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		},
	);
	if (!response.ok) {
		throw new Error(
			`Failed to load Mappedin MVF metadata for venue ${venueID}: ${response.status}`,
		);
	}

	return response.json();
}

function resolveLocationAnchors(
	entity: Readonly<Location | LocationInstance>,
	locationMap: ReadonlyMap<string, Location>,
): MVFGeometryAnchor[] {
	const anchors = entity.geometryAnchors;
	if (anchors !== undefined && anchors.length !== 0) {
		return anchors;
	}

	if ("parentId" in entity) {
		return locationMap.get(entity.parentId)?.geometryAnchors ?? [];
	}

	return [];
}

function resolveLocationName(
	entity: Readonly<Location | LocationInstance>,
	locationMap: ReadonlyMap<string, Location>,
): string | undefined {
	//Try to get the name from the entity itself
	const ownName = entity.details?.name;
	if (ownName !== undefined) {
		return ownName;
	}

	//Try to get the name from the parent location
	if ("parentId" in entity) {
		return locationMap.get(entity.parentId)?.details?.name;
	}

	return undefined;
}

function resolveLabelsForFloor(
	floorID: string,
	geometryFeatures: GeometryFeature[],
	locations: Location[],
	locationInstances: LocationInstance[],
): FeatureCollection<Point, MappedinMVFLabelProperties> {
	//Create a map of geometry features to their ID
	const geometryByID = new Map<string, GeometryFeature>();
	for (const feature of geometryFeatures) {
		const geometryID = feature.properties?.id;
		if (geometryID !== undefined) {
			geometryByID.set(geometryID, feature);
		}
	}

	//Create a map of locations to their ID
	const locationMap = new Map<string, Location>(
		locations.map((location) => [location.id, location]),
	);

	//Create a list of all locations
	const allEntities: (Location | LocationInstance)[] = [
		...locations,
		...locationInstances,
	];

	//Convert the locations to GeoJSON features
	const features: Feature<Point, MappedinMVFLabelProperties>[] = [];
	for (const entity of allEntities) {
		//Get the label for this location
		const label = resolveLocationName(entity, locationMap);
		if (label === undefined) {
			continue;
		}

		//Get the anchors for this location
		const anchors = resolveLocationAnchors(entity, locationMap).filter(
			(candidate) => candidate.floorId === floorID,
		);
		if (anchors.length === 0) {
			continue;
		}

		for (const anchor of anchors) {
			//Find the geometry feature for this anchor
			const targetGeometry = geometryByID.get(anchor.geometryId);
			if (targetGeometry === undefined) {
				continue;
			}

			//Resolve where to put the label
			const center = pointOnFeature(targetGeometry.geometry);

			//Add the feature to the list
			features.push({
				...center,
				properties: {
					text: label,
				},
			});
		}
	}

	return {
		type: "FeatureCollection",
		features,
	};
}

export async function parseMappedinMVF(
	zipData: ArrayBuffer,
	mappedinStyleCollection?: Readonly<MappedinStyleCollection>,
): Promise<MappedinMVFVenue> {
	//Load the MVF bundle
	const bundle = await readMVFBundle(zipData);

	//Load the bundle features
	const floorFeatures = bundle.floors?.features ?? [];
	const defaultStyles = bundle.defaultStyle ?? {};
	const locations = bundle.locations ?? [];
	const locationInstances = bundle.locationInstances ?? [];
	const geometryByFloor = bundle.geometry;

	function createAnchorKey(anchor: MVFGeometryAnchor): string {
		return anchor.floorId + " " + anchor.geometryId;
	}

	//Create a map of geometry features to their style
	const styleMap = new Map<string, DefaultStyle>();
	for (const [styleName, defaultStyle] of Object.entries(defaultStyles)) {
		let style = defaultStyle;

		//Apply the style override if it exists
		const styleOverride = mappedinStyleCollection?.[styleName];
		if (styleOverride !== undefined) {
			style = {
				...defaultStyle,
				...styleOverride,
			};
		}

		//Index this style for its anchors
		for (const anchor of style.geometryAnchors) {
			styleMap.set(createAnchorKey(anchor), style);
		}
	}

	//Parse the floors
	const parsedFloors: Record<string, MappedinMVFFloor> = {};
	for (const floorFeature of floorFeatures) {
		//Get the floor ID
		const floorID = floorFeature.properties?.id;
		if (floorID === undefined) {
			continue;
		}

		//Get the geometry features for this floor
		const floorGeometry = geometryByFloor[floorID];
		if (floorGeometry === undefined) {
			continue;
		}
		const features: Feature<Geometry, MappedinMVFGeometryProperties>[] = [];
		for (const geometryFeature of floorGeometry.features) {
			//Ignore if this feature is not a polygon
			if (
				geometryFeature.geometry.type !== "Polygon" &&
				geometryFeature.geometry.type !== "MultiPolygon" &&
				geometryFeature.geometry.type !== "LineString" &&
				geometryFeature.geometry.type !== "MultiLineString"
			) {
				continue;
			}

			//Retrieve the style for this geometry feature
			const anchorKey = createAnchorKey({
				floorId: floorID,
				geometryId: geometryFeature.properties.id,
			});
			const style = styleMap.get(anchorKey);
			if (style === undefined) {
				continue;
			}

			//Ignore hidden features
			if (style.hidden) {
				continue;
			}

			//Initialize the geometry style properties
			const styleProperties: MappedinMVFGeometryProperties = {
				color: style.color,
				opacity: style.opacity,
				height: style.height,
				altitude: style.altitude,
			};

			//Buffer the geometry
			let effectiveFeature: Feature | undefined;
			if (style.buffer !== 0) {
				effectiveFeature = buffer(geometryFeature, style.buffer, {
					units: "meters",
				});

				//If we failed to buffer the geometry, skip this feature
				if (effectiveFeature === undefined) {
					continue;
				}
			} else {
				effectiveFeature = geometryFeature;
			}

			//Add the style properties to the feature
			features.push({
				...effectiveFeature,
				properties: {
					...effectiveFeature.properties,
					...styleProperties,
				},
			});
		}

		//Add the floor
		parsedFloors[floorID] = {
			id: floorID,
			elevation: floorFeature.properties?.elevation,
			geometry: {
				type: "FeatureCollection",
				features,
			},
			labels: resolveLabelsForFloor(
				floorID,
				floorGeometry.features,
				locations,
				locationInstances,
			),
		};
	}

	return {
		floors: parsedFloors,
	};
}

export async function downloadAndParseMappedinMVF(
	url: string,
	mappedinStyleCollection?: Readonly<MappedinStyleCollection>,
): Promise<MappedinMVFVenue> {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`Failed to download Mappedin MVF: ${response.status}`);
	}

	return parseMappedinMVF(
		await response.arrayBuffer(),
		mappedinStyleCollection,
	);
}

export async function downloadAndParseMappedinVenueMVF(
	venueID: string,
	accessToken: string,
	mappedinStyleCollection?: Readonly<MappedinStyleCollection>,
): Promise<MappedinMVFVenue> {
	const mvf = await fetchMappedinVenueMVF(venueID, accessToken);
	return downloadAndParseMappedinMVF(mvf.url, mappedinStyleCollection);
}
